import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface CompanyDetailsFormProps {
  email: string;
  onSuccess: () => void;
}

const supportedIndustries = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "finance", label: "Finance" },
  { value: "logistics", label: "Logistics" },
  { value: "tech", label: "Technology" },
  { value: "realestate", label: "Real Estate" },
  { value: "health", label: "Health" },
  { value: "universal", label: "Universal / Custom Industry" },
];

export const CompanyDetailsForm = ({ email, onSuccess }: CompanyDetailsFormProps) => {
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [businessSize, setBusinessSize] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          company_name: companyName,
          employee_count: parseInt(employeeCount),
          business_size: businessSize,
          industry: industry,
        });

      if (error) throw error;
      toast.success("Company details saved successfully!");
      onSuccess();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold">Company Details</h2>
        <p className="text-muted-foreground mt-2">Please provide your company information</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input placeholder="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
        <Input type="number" placeholder="Number of Employees" value={employeeCount} onChange={(e) => setEmployeeCount(e.target.value)} required />
        <Select value={businessSize} onValueChange={setBusinessSize} required>
          <SelectTrigger><SelectValue placeholder="Select Business Size" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Small Business</SelectItem>
            <SelectItem value="medium">Small-Medium Business</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={industry} onValueChange={setIndustry} required>
          <SelectTrigger><SelectValue placeholder="Select Your Industry" /></SelectTrigger>
          <SelectContent>
            {supportedIndustries.map((ind) => (
              <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Saving..." : "Complete Registration"}
        </Button>
      </form>
    </Card>
  );
};
